/* Inner Compass — Privacyverklaring.
 * Dutch is the authoritative version. English is a faithful translation.
 * Last updated: 1 October 2026. */

export const privacyContent = {
  nl: [
    {
      heading: "1. Over deze privacyverklaring",
      body: [
        "Inner Compass respecteert je privacy en gaat zorgvuldig om met persoonsgegevens.",
        "Deze privacyverklaring beschrijft welke persoonsgegevens Inner Compass verwerkt wanneer je onze website, mobiele applicatie, webapplicatie en andere diensten gebruikt, waarom wij deze gegevens verwerken, met wie gegevens eventueel worden gedeeld, hoe lang gegevens worden bewaard en welke rechten je hebt.",
        "Inner Compass is bedoeld als hulpmiddel voor mentaal welzijn. Omdat informatie die je binnen Inner Compass deelt gevoelige informatie over je welzijn of gezondheid kan bevatten, besteden wij bijzondere aandacht aan privacy en gegevensbescherming.",
      ],
    },
    {
      heading: "2. Wie is verantwoordelijk?",
      body: [
        "Voor verwerkingen waarvoor Inner Compass zelf het doel en de middelen bepaalt, is Inner Compass de verwerkingsverantwoordelijke.",
        "Inner Compass",
        "Eenmanszaak",
        "Nederland",
        "KvK-nummer: 42129904",
      ],
      list: [
        "Website: https://innercompass.tech",
        "Algemene vragen: contact@innercompass.tech",
        "Ondersteuning: support@innercompass.tech",
        "Privacyvragen: privacy@innercompass.tech",
      ],
      note:
        "Bij zakelijke dienstverlening kan de rolverdeling tussen Inner Compass en de betreffende organisatie verschillen. Waar een organisatie bepaalt voor welke doeleinden en op welke wijze persoonsgegevens via Inner Compass worden verwerkt, kunnen aanvullende privacy- en verwerkersafspraken gelden.",
    },
    {
      heading: "3. Welke persoonsgegevens verwerken wij?",
      body: "Welke gegevens we verwerken hangt af van de functies die je gebruikt.",
    },
    { subheading: "Accountgegevens", body: "Wij kunnen verwerken:",
      list: [
        "naam;",
        "e-mailadres;",
        "account-ID;",
        "taal en accountvoorkeuren;",
        "organisatie, team of rol wanneer je via een organisatie deelneemt;",
        "informatie over accountstatus;",
        "beveiligings- en authenticatiegegevens.",
      ],
      note: "Wachtwoorden worden niet als leesbare tekst opgeslagen.",
    },
    { subheading: "Welzijnsgegevens",
      body: "Wanneer je deze functies gebruikt, kun je informatie delen over onder andere:",
      list: [
        "stemming;",
        "mentale en emotionele toestand;",
        "check-ins;",
        "antwoorden en toelichtingen bij check-ins;",
        "reflecties;",
        "dagboeknotities;",
        "hersteldoelen;",
        "herstelplannen en strategieën;",
        "ervaren stress, energie, slaap of welzijn;",
        "persoonlijke omstandigheden die je zelf beschrijft;",
        "andere informatie die je vrijwillig invoert.",
      ],
      note: "Deze informatie kan afhankelijk van de inhoud kwalificeren als bijzondere persoonsgegevens, waaronder gegevens over gezondheid.",
    },
    { subheading: "AI-gesprekken",
      body: "Wanneer je de AI-metgezel gebruikt, kunnen we verwerken:",
      list: [
        "jouw berichten;",
        "antwoorden die door de AI worden gegenereerd;",
        "relevante gesprekscontext;",
        "door jou verstrekte informatie die nodig is om het gesprek voort te zetten;",
        "indien ingeschakeld en toegestaan: informatie die wordt gebruikt voor persoonlijke herinnering of context.",
      ],
    },
    { subheading: "Persoonlijke herinneringen en context",
      body: [
        "Wanneer deze functionaliteit beschikbaar en door jou toegestaan is, kan Inner Compass relevante informatie uit eerdere interacties bewaren om toekomstige ondersteuning persoonlijker en consistenter te maken.",
        "Waar daarvoor toestemming vereist is, wordt deze verwerking alleen uitgevoerd wanneer die toestemming is gegeven.",
      ],
    },
    { subheading: "Veiligheids- en crisisfuncties",
      body: [
        "Inner Compass kan tekst automatisch analyseren om taal te herkennen die mogelijk wijst op een ernstige crisis of acuut veiligheidsrisico.",
        "Deze analyse is bedoeld om passende veiligheidsinformatie of hulpbronnen te kunnen tonen.",
        "Inner Compass is geen alarmcentrale of crisisdienst en kan niet garanderen dat iedere crisissituatie wordt herkend.",
      ],
    },
    { subheading: "Noodcontacten en veilige plek",
      body: "Als je deze functies gebruikt, kunnen we gegevens verwerken die je zelf invoert over:",
      list: [
        "noodcontacten;",
        "relatie tot het noodcontact;",
        "contactgegevens;",
        "instellingen rondom je veilige plek.",
      ],
      note: "Voer alleen persoonsgegevens van anderen in wanneer je daarvoor een passende reden hebt.",
    },
    { subheading: "Zakelijke accounts",
      body: "Wanneer je Inner Compass via een werkgever of andere organisatie gebruikt, kunnen we daarnaast verwerken:",
      list: [
        "organisatie;",
        "team;",
        "zakelijke rol;",
        "uitnodigingsstatus;",
        "technische accountkoppeling met de organisatie.",
      ],
    },
    { body: [
        "Het gebruik van een zakelijk account betekent niet automatisch dat een werkgever, leidinggevende of HR toegang krijgt tot je persoonlijke dagboek, privégesprekken of individuele welzijnsgegevens.",
        "Welke informatie binnen een organisatie beschikbaar is, hangt af van de betreffende functionaliteit, rollen, privacy-instellingen, toestemmingen en toepasselijke afspraken.",
        "Wanneer een functie individuele informatie met een zorgprofessional, bedrijfsarts of andere bevoegde rol kan delen, moet voor de gebruiker duidelijk zijn welke gegevens worden gedeeld en op welke basis.",
      ],
    },
    {
      heading: "4. Waarvoor gebruiken we persoonsgegevens?",
      body: "Wij kunnen persoonsgegevens gebruiken om:",
      list: [
        "je account aan te maken en te beveiligen;",
        "Inner Compass te laten functioneren;",
        "check-ins en welzijnsinformatie op te slaan;",
        "persoonlijke inzichten en patronen weer te geven;",
        "AI-gesprekken mogelijk te maken;",
        "gesprekken relevanter en consistenter te maken;",
        "door jou gekozen herinneringen of context te gebruiken;",
        "herstelplannen en persoonlijke strategieën te ondersteunen;",
        "veiligheidsfuncties uit te voeren;",
        "technische problemen te onderzoeken;",
        "misbruik en beveiligingsincidenten te voorkomen;",
        "klantenservice te leveren;",
        "te voldoen aan wettelijke verplichtingen;",
        "de betrouwbaarheid en bruikbaarheid van Inner Compass te verbeteren.",
      ],
    },
    {
      heading: "5. Rechtsgrondslagen",
      body: "Afhankelijk van de verwerking baseren wij verwerking onder de Algemene verordening gegevensbescherming (AVG) op een of meer toepasselijke grondslagen.",
    },
    { subheading: "Uitvoering van een overeenkomst", body: "Wanneer verwerking noodzakelijk is om een door jou gevraagde dienst te leveren." },
    { subheading: "Toestemming", body: "Wanneer toestemming vereist of passend is, waaronder voor bepaalde gevoelige of optionele gegevensverwerkingen." },
    { subheading: "Gerechtvaardigd belang", body: "Bijvoorbeeld voor beveiliging, fraudepreventie, technische betrouwbaarheid en bescherming van onze diensten, voor zover jouw belangen en grondrechten niet zwaarder wegen." },
    { subheading: "Wettelijke verplichting", body: "Wanneer wij gegevens moeten verwerken of bewaren om aan een wettelijke verplichting te voldoen." },
    { body: "Voor bijzondere persoonsgegevens geldt daarnaast een toepasselijke uitzondering op het verbod om bijzondere persoonsgegevens te verwerken. Wanneer wij hiervoor uitdrukkelijke toestemming gebruiken, vragen wij die afzonderlijk en duidelijk." },
    {
      heading: "6. Toestemming intrekken",
      body: [
        "Wanneer verwerking op jouw toestemming is gebaseerd, kun je die toestemming weer intrekken.",
        "Het intrekken van toestemming heeft geen terugwerkende kracht. Verwerking die vóór de intrekking rechtmatig heeft plaatsgevonden, wordt daardoor niet achteraf onrechtmatig.",
        "Waar beschikbaar kun je toestemmingen beheren binnen Inner Compass. Je kunt daarnaast contact opnemen via privacy@innercompass.tech.",
        "Het intrekken van bepaalde toestemmingen kan betekenen dat specifieke functies niet meer beschikbaar zijn.",
      ],
    },
    {
      heading: "7. Kunstmatige intelligentie",
      body: [
        "Inner Compass gebruikt kunstmatige intelligentie om ondersteunende gesprekken en bepaalde persoonlijke functies mogelijk te maken.",
        "Voor het genereren van AI-reacties kan informatie worden verwerkt door externe AI-dienstverleners die wij voor deze functionaliteit inschakelen.",
        "Inner Compass gebruikt momenteel onder meer technologie van Anthropic en, waar van toepassing, OpenAI.",
        "Wij streven ernaar alleen informatie te verwerken of verstrekken die noodzakelijk is voor de betreffende functionaliteit en passende technische en contractuele waarborgen toe te passen.",
        "AI-uitvoer wordt automatisch gegenereerd en kan onjuist, onvolledig of ongepast zijn.",
        "Inner Compass gebruikt AI niet als vervanging voor een arts, psycholoog, therapeut of andere gekwalificeerde zorgverlener.",
      ],
    },
    {
      heading: "8. Geautomatiseerde verwerking",
      body: "Inner Compass kan geautomatiseerde verwerking gebruiken voor onder andere:",
      list: [
        "AI-reacties;",
        "persoonlijke inzichten;",
        "patroonherkenning;",
        "veiligheids- en crisisclassificatie.",
      ],
      note:
        "Deze functies kunnen invloed hebben op de informatie of ondersteuning die binnen de Dienst aan jou wordt getoond. Inner Compass gebruikt deze functies niet om zelfstandig besluiten met rechtsgevolgen of vergelijkbare aanzienlijke gevolgen voor jou te nemen, tenzij een dergelijke functie in de toekomst uitdrukkelijk wordt geïntroduceerd en daarvoor een geldige wettelijke basis en passende waarborgen bestaan.",
    },
    {
      heading: "9. Gebruik via organisaties",
      body: [
        "Bij gebruik via een werkgever of andere organisatie hanteren wij als uitgangspunt dat persoonlijke welzijnsinformatie niet automatisch zichtbaar wordt voor leidinggevenden of HR.",
        "Organisaties kunnen, afhankelijk van het product en de gemaakte afspraken, toegang krijgen tot administratieve informatie en privacyvriendelijke of geaggregeerde inzichten.",
        "Wanneer een functie het mogelijk maakt individuele informatie te delen met een zorgprofessional, bedrijfsarts of andere bevoegde persoon, wordt duidelijk gemaakt welke informatie wordt gedeeld, met wie, voor welk doel en op welke grondslag.",
        "Waar toestemming vereist is, moet deze vrij kunnen worden gegeven en ingetrokken volgens de daarvoor beschikbare functionaliteit.",
      ],
    },
    {
      heading: "10. Met wie delen wij persoonsgegevens?",
      body: [
        "Wij verkopen persoonsgegevens niet.",
        "Wij verkopen geen gesprekken, dagboekinformatie of welzijnsgegevens aan adverteerders.",
        "Voor het leveren van onze diensten kunnen wij gebruikmaken van leveranciers voor bijvoorbeeld:",
      ],
      list: [
        "cloudhosting;",
        "databases;",
        "AI-verwerking;",
        "transactionele e-mail;",
        "beveiliging;",
        "technische monitoring;",
        "foutdiagnostiek;",
        "privacyvriendelijke analytics, indien gebruikt.",
      ],
      note:
        "Voor zover partijen persoonsgegevens namens ons verwerken, treffen wij passende afspraken over gegevensbescherming. Wij kunnen persoonsgegevens verstrekken wanneer dit wettelijk verplicht is of noodzakelijk is voor de vaststelling, uitoefening of verdediging van juridische aanspraken.",
    },
    {
      heading: "11. Internationale doorgifte",
      body: [
        "Sommige dienstverleners kunnen persoonsgegevens buiten de Europese Economische Ruimte verwerken.",
        "Wanneer persoonsgegevens internationaal worden doorgegeven, gebruiken wij waar vereist passende waarborgen, zoals een adequaatheidsbesluit, Standard Contractual Clauses of andere wettelijk toegestane mechanismen.",
      ],
    },
    {
      heading: "12. Bewaartermijnen",
      body: [
        "Wij bewaren persoonsgegevens niet langer dan noodzakelijk voor het doel waarvoor ze zijn verzameld, tenzij een langere bewaartermijn wettelijk vereist is.",
        "Als uitgangspunt:",
      ],
      list: [
        "accountgegevens: zolang je account actief is en vervolgens gedurende de periode die noodzakelijk is voor verwijdering en wettelijke verplichtingen;",
        "persoonlijke welzijnscontent: zolang je account actief is of totdat je de betreffende informatie verwijdert;",
        "AI-gesprekken en persoonlijke context: zolang deze voor de betreffende functie worden bewaard of totdat ze volgens de beschikbare functies worden verwijderd;",
        "technische en beveiligingslogs: gedurende een beperkte periode die past bij het doel;",
        "verwijderde accountgegevens: worden volgens het accountverwijderingsproces uit actieve systemen verwijderd;",
        "back-ups: kunnen na accountverwijdering tijdelijk blijven bestaan en worden volgens de geldende back-upcyclus verwijderd of overschreven, behoudens wettelijke verplichtingen.",
      ],
    },
    {
      heading: "13. Beveiliging",
      body: ["We nemen passende technische en organisatorische maatregelen om persoonsgegevens te beschermen.", "Deze kunnen onder andere bestaan uit:"],
      list: [
        "versleutelde verbindingen via HTTPS/TLS;",
        "beveiligde authenticatie;",
        "gehashte wachtwoorden;",
        "toegangsbeperking;",
        "scheiding van rollen en rechten;",
        "logging van relevante beveiligingsgebeurtenissen;",
        "beveiligde cloudinfrastructuur;",
        "beperking van toegang tot productiegegevens.",
      ],
      note: "Geen enkel digitaal systeem kan absolute veiligheid garanderen.",
    },
    {
      heading: "14. Jouw privacyrechten",
      body: "Onder de AVG kun je, afhankelijk van de situatie, recht hebben op:",
      list: [
        "inzage in je persoonsgegevens;",
        "rectificatie van onjuiste persoonsgegevens;",
        "verwijdering;",
        "beperking van verwerking;",
        "overdraagbaarheid van gegevens;",
        "bezwaar tegen bepaalde verwerkingen;",
        "intrekking van toestemming;",
        "informatie over bepaalde vormen van geautomatiseerde verwerking.",
      ],
      note:
        "Een verzoek kan worden gestuurd naar privacy@innercompass.tech. We kunnen je vragen je identiteit te bevestigen wanneer dit noodzakelijk is om persoonsgegevens te beschermen. Je hebt daarnaast het recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens.",
    },
    {
      heading: "15. Kinderen",
      body: [
        "Inner Compass is niet bedoeld voor kinderen jonger dan 16 jaar.",
        "Wij verzamelen niet bewust persoonsgegevens van kinderen jonger dan 16 jaar zonder een daarvoor geldige wettelijke basis en, waar vereist, toestemming van een ouder of wettelijke vertegenwoordiger.",
        "Wanneer je denkt dat een kind jonger dan 16 jaar persoonsgegevens aan Inner Compass heeft verstrekt, kun je contact opnemen via privacy@innercompass.tech.",
      ],
    },
    {
      heading: "16. Cookies en vergelijkbare technologieën",
      body: [
        "De website en webapplicatie kunnen noodzakelijke cookies of vergelijkbare technologieën gebruiken om de Dienst veilig en correct te laten functioneren.",
        "Meer informatie staat in ons Cookiebeleid op /cookies.",
      ],
    },
    {
      heading: "17. Wijzigingen",
      body: [
        "Deze privacyverklaring kan worden aangepast wanneer Inner Compass, onze gegevensverwerkingen of toepasselijke wetgeving veranderen.",
        "Bij belangrijke wijzigingen informeren wij gebruikers op passende wijze.",
        "De actuele versie staat op https://innercompass.tech/privacy.",
      ],
    },
    {
      heading: "18. Contact",
      body: ["Inner Compass", "Eenmanszaak", "KvK: 42129904", "Nederland"],
      list: [
        "Algemeen: contact@innercompass.tech",
        "Ondersteuning: support@innercompass.tech",
        "Privacy: privacy@innercompass.tech",
      ],
    },
  ],
  en: [
    {
      heading: "1. About this privacy statement",
      body: [
        "Inner Compass respects your privacy and handles personal data with care.",
        "This privacy statement describes which personal data Inner Compass processes when you use our website, mobile application, web application and other services, why we process this data, with whom data may be shared, how long data is retained, and which rights you have.",
        "Inner Compass is intended as a tool for mental wellbeing. Because information you share within Inner Compass may contain sensitive information about your wellbeing or health, we pay particular attention to privacy and data protection.",
      ],
    },
    {
      heading: "2. Who is responsible?",
      body: [
        "For processing where Inner Compass itself determines the purposes and means, Inner Compass is the data controller.",
        "Inner Compass",
        "Sole proprietorship (eenmanszaak)",
        "The Netherlands",
        "Chamber of Commerce (KvK) number: 42129904",
      ],
      list: [
        "Website: https://innercompass.tech",
        "General enquiries: contact@innercompass.tech",
        "Support: support@innercompass.tech",
        "Privacy enquiries: privacy@innercompass.tech",
      ],
      note:
        "For business services, the roles between Inner Compass and the organisation involved may differ. Where an organisation determines the purposes and means of processing personal data via Inner Compass, additional privacy and data-processing agreements may apply.",
    },
    { heading: "3. Which personal data do we process?", body: "The data we process depends on the features you use." },
    { subheading: "Account data", body: "We may process:",
      list: ["name;", "email address;", "account ID;", "language and account preferences;", "organisation, team or role when you participate through an organisation;", "information about account status;", "security and authentication data."],
      note: "Passwords are never stored as readable text.",
    },
    { subheading: "Wellbeing data", body: "When you use these features you may share information about, among other things:",
      list: ["mood;", "mental and emotional state;", "check-ins;", "answers and explanations provided during check-ins;", "reflections;", "journal entries;", "recovery goals;", "recovery plans and strategies;", "experienced stress, energy, sleep or wellbeing;", "personal circumstances you describe yourself;", "other information you voluntarily enter."],
      note: "Depending on its content, this information may qualify as a special category of personal data, including health-related data.",
    },
    { subheading: "AI conversations", body: "When you use the AI companion we may process:",
      list: ["your messages;", "replies generated by the AI;", "relevant conversation context;", "information you provide that is necessary to continue the conversation;", "if enabled and permitted: information used for personal memory or context."],
    },
    { subheading: "Personal memory and context",
      body: ["When this functionality is available and permitted by you, Inner Compass can retain relevant information from earlier interactions to make future support more personal and consistent.", "Where consent is required for this, this processing only takes place when that consent has been given."],
    },
    { subheading: "Safety and crisis features",
      body: ["Inner Compass can automatically analyse text to recognise language that may indicate a serious crisis or acute safety risk.", "This analysis is intended to be able to show appropriate safety information or resources.", "Inner Compass is not an emergency centre or crisis service and cannot guarantee that every crisis situation is recognised."],
    },
    { subheading: "Emergency contacts and safe place",
      body: "If you use these features we may process information you enter yourself about:",
      list: ["emergency contacts;", "relationship to the emergency contact;", "contact details;", "settings relating to your safe place."],
      note: "Only enter other people's personal data when you have an appropriate reason to do so.",
    },
    { subheading: "Business accounts", body: "When you use Inner Compass through an employer or other organisation, we may additionally process:",
      list: ["organisation;", "team;", "business role;", "invitation status;", "technical account linkage to the organisation."],
    },
    { body: [
        "Using a business account does not automatically mean that an employer, manager or HR gets access to your personal journal, private conversations or individual wellbeing data.",
        "Which information is available within an organisation depends on the feature in question, roles, privacy settings, consents and applicable agreements.",
        "When a feature can share individual information with a healthcare professional, occupational physician or another authorised role, the user must be clearly informed about which data is shared and on which basis.",
      ],
    },
    {
      heading: "4. For what do we use personal data?",
      body: "We may use personal data to:",
      list: [
        "create and secure your account;",
        "operate Inner Compass;",
        "store check-ins and wellbeing information;",
        "display personal insights and patterns;",
        "enable AI conversations;",
        "make conversations more relevant and consistent;",
        "use memories or context you have chosen;",
        "support recovery plans and personal strategies;",
        "run safety features;",
        "investigate technical issues;",
        "prevent abuse and security incidents;",
        "provide customer service;",
        "comply with legal obligations;",
        "improve the reliability and usability of Inner Compass.",
      ],
    },
    {
      heading: "5. Legal bases",
      body: "Depending on the processing, we rely on one or more applicable legal bases under the General Data Protection Regulation (GDPR).",
    },
    { subheading: "Performance of a contract", body: "Where processing is necessary to provide a service you have requested." },
    { subheading: "Consent", body: "Where consent is required or appropriate, including for certain sensitive or optional data processing." },
    { subheading: "Legitimate interest", body: "For example for security, fraud prevention, technical reliability and protection of our services, insofar as your interests and fundamental rights do not override these." },
    { subheading: "Legal obligation", body: "When we must process or retain data to comply with a legal obligation." },
    { body: "For special-category personal data, an applicable exception to the prohibition on processing such data additionally applies. Where we rely on explicit consent for this, we request it separately and clearly." },
    {
      heading: "6. Withdrawing consent",
      body: [
        "Where processing is based on your consent, you can withdraw that consent again.",
        "Withdrawing consent is not retroactive. Processing that lawfully took place before the withdrawal does not become unlawful retrospectively.",
        "Where available you can manage consents within Inner Compass. You may also contact us via privacy@innercompass.tech.",
        "Withdrawing certain consents may mean that specific features are no longer available.",
      ],
    },
    {
      heading: "7. Artificial intelligence",
      body: [
        "Inner Compass uses artificial intelligence to enable supportive conversations and certain personal features.",
        "To generate AI replies, information may be processed by external AI service providers that we engage for this functionality.",
        "Inner Compass currently uses, among others, technology from Anthropic and, where applicable, OpenAI.",
        "We aim to process or disclose only information necessary for the functionality in question and to apply appropriate technical and contractual safeguards.",
        "AI output is generated automatically and can be inaccurate, incomplete or inappropriate.",
        "Inner Compass does not use AI as a replacement for a doctor, psychologist, therapist or other qualified healthcare provider.",
      ],
    },
    {
      heading: "8. Automated processing",
      body: "Inner Compass may use automated processing for, among other things:",
      list: ["AI replies;", "personal insights;", "pattern recognition;", "safety and crisis classification."],
      note:
        "These features can influence the information or support shown to you within the Service. Inner Compass does not use these features to take autonomous decisions that produce legal effects or similarly significant effects for you, unless such a feature is explicitly introduced in the future with a valid legal basis and appropriate safeguards.",
    },
    {
      heading: "9. Use through organisations",
      body: [
        "When used through an employer or other organisation, our starting point is that personal wellbeing information is not automatically visible to managers or HR.",
        "Depending on the product and the agreements made, organisations may receive access to administrative information and privacy-preserving or aggregated insights.",
        "Where a feature enables sharing of individual information with a healthcare professional, occupational physician or other authorised person, it will be clear which information is shared, with whom, for which purpose and on which basis.",
        "Where consent is required, it must be possible to give and withdraw it freely via the functionality available for this.",
      ],
    },
    {
      heading: "10. With whom do we share personal data?",
      body: [
        "We do not sell personal data.",
        "We do not sell conversations, journal information or wellbeing data to advertisers.",
        "To deliver our services we may use suppliers for, for example:",
      ],
      list: [
        "cloud hosting;",
        "databases;",
        "AI processing;",
        "transactional email;",
        "security;",
        "technical monitoring;",
        "error diagnostics;",
        "privacy-friendly analytics, if used.",
      ],
      note:
        "To the extent that parties process personal data on our behalf, we put appropriate data-protection agreements in place. We may disclose personal data where legally required or necessary for the establishment, exercise or defence of legal claims.",
    },
    {
      heading: "11. International transfers",
      body: [
        "Some service providers may process personal data outside the European Economic Area.",
        "Where personal data is transferred internationally, we use appropriate safeguards where required, such as an adequacy decision, Standard Contractual Clauses or other legally permitted mechanisms.",
      ],
    },
    {
      heading: "12. Retention periods",
      body: ["We do not retain personal data longer than necessary for the purpose for which it was collected, unless a longer retention period is legally required.", "As a starting point:"],
      list: [
        "account data: for as long as your account is active and subsequently for the period necessary for deletion and legal obligations;",
        "personal wellbeing content: for as long as your account is active or until you delete the information;",
        "AI conversations and personal context: for as long as retained for the relevant feature or until deleted using the available functions;",
        "technical and security logs: for a limited period appropriate to the purpose;",
        "deleted account data: is removed from active systems in line with the account-deletion process;",
        "back-ups: may temporarily persist after account deletion and are deleted or overwritten in accordance with the applicable back-up cycle, subject to legal obligations.",
      ],
    },
    {
      heading: "13. Security",
      body: ["We take appropriate technical and organisational measures to protect personal data.", "These may include, among others:"],
      list: [
        "encrypted connections via HTTPS/TLS;",
        "secure authentication;",
        "hashed passwords;",
        "access restriction;",
        "separation of roles and permissions;",
        "logging of relevant security events;",
        "secure cloud infrastructure;",
        "restricted access to production data.",
      ],
      note: "No digital system can guarantee absolute security.",
    },
    {
      heading: "14. Your privacy rights",
      body: "Under the GDPR, depending on the circumstances, you may have the right to:",
      list: [
        "access to your personal data;",
        "rectification of inaccurate personal data;",
        "erasure;",
        "restriction of processing;",
        "data portability;",
        "object to certain processing;",
        "withdraw consent;",
        "information about certain forms of automated processing.",
      ],
      note:
        "A request can be sent to privacy@innercompass.tech. We may ask you to confirm your identity where this is necessary to protect personal data. You also have the right to lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).",
    },
    {
      heading: "15. Children",
      body: [
        "Inner Compass is not intended for children under the age of 16.",
        "We do not knowingly collect personal data from children under 16 without a valid legal basis and, where required, consent from a parent or legal representative.",
        "If you believe that a child under 16 has provided personal data to Inner Compass, you can contact us via privacy@innercompass.tech.",
      ],
    },
    {
      heading: "16. Cookies and similar technologies",
      body: [
        "The website and web application may use strictly necessary cookies or similar technologies to let the Service function safely and correctly.",
        "More information is set out in our Cookie Policy at /cookies.",
      ],
    },
    {
      heading: "17. Changes",
      body: [
        "This privacy statement may be adjusted when Inner Compass, our data-processing activities or applicable law change.",
        "In the event of material changes we inform users in an appropriate way.",
        "The current version is available at https://innercompass.tech/privacy.",
      ],
    },
    {
      heading: "18. Contact",
      body: ["Inner Compass", "Sole proprietorship", "Chamber of Commerce (KvK): 42129904", "The Netherlands"],
      list: [
        "General: contact@innercompass.tech",
        "Support: support@innercompass.tech",
        "Privacy: privacy@innercompass.tech",
      ],
    },
  ],
};
